import { createContext, useContext, useState, type ReactNode } from 'react'
import { useIsSupplierRole } from '@omnea-app/fe-auth/UserSessionProvider'
import { useLdReadyWithTimeout } from '@omnea-app/fe-org-settings/useLdReadyWithTimeout'
import { useAppShellFlags } from '../launch-darkly'

export type ShellVariant = 'classic' | 'appShell'

// Signed-out pages sit outside the provider and always get the classic shell.
const ShellVariantContext = createContext<ShellVariant | null>('classic')

/** The shell this session gets, or `null` while LaunchDarkly hasn't answered. */
export const useShellVariant = () => useContext(ShellVariantContext)

/**
 * Decides once which shell this session gets. The answer holds for the rest of the session: swapping frames remounts
 * the page, so a flag change mid-session waits for the next load. Suppliers never get the app shell, so they don't wait.
 */
export const ShellVariantProvider = ({ children }: { children: ReactNode }) => {
  const isSupplier = useIsSupplierRole()
  const isLdReady = useLdReadyWithTimeout()
  const { RELEASE_APP_SHELL } = useAppShellFlags()
  const [variant, setVariant] = useState<ShellVariant | null>(null)

  if (variant == null && (isSupplier || isLdReady)) {
    setVariant(!isSupplier && RELEASE_APP_SHELL === true ? 'appShell' : 'classic')
  }

  return <ShellVariantContext.Provider value={variant}>{children}</ShellVariantContext.Provider>
}
